import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailPlus, MailX } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

export const ownerAddress = (lead) => lead?.contact_email || lead?.email || "";

export const NoEmailTag = ({ lead }) => ownerAddress(lead) ? null : (
  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[11px] font-semibold whitespace-nowrap shrink-0" data-testid={`lead-no-email-${lead.id}`}>
    <MailX className="h-3 w-3" />No email
  </span>
);

export const OwnerEmailedTag = ({ lead, size = "text-[11px]" }) => lead.owner_emailed_at ? (
  <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white font-semibold ${size}`} data-testid={`lead-owner-emailed-${lead.id}`}>
    <MailPlus className="h-3 w-3" />Owner got email{lead.owner_emailed_pitch ? ` (${lead.owner_emailed_pitch})` : ""} · {new Date(lead.owner_emailed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
  </span>
) : null;

const mailtoLink = (to, bcc, p) =>
  `mailto:${encodeURIComponent(to)}?bcc=${encodeURIComponent(bcc)}&subject=${encodeURIComponent(p.subject)}&body=${encodeURIComponent(p.lines.join("\n\n"))}`;

// Opens the rep's own email app (sales@paywick.net) pre-filled, then records it once they confirm it went out.
export const OwnerPitchDialog = ({ lead, onClose, onSent }) => {
  const [pitch, setPitch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const [key, setKey] = useState(null);
  const chosen = pitch?.pitches.find((p) => p.key === key);
  useEffect(() => {
    if (lead && !pitch) api.get("/owner-pitch").then((r) => setPitch(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [lead, pitch]);
  useEffect(() => { if (!lead) { setOpened(false); setKey(null); } }, [lead]);

  const confirmSent = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/email-owner`, { pitch: key });
      toast.success(`Marked: owner got email (${data.owner_emailed_to})`);
      onSent(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const to = ownerAddress(lead);
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="owner-pitch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MailPlus className="h-5 w-5 text-emerald-600" />{opened ? "Did you send it?" : "Email the owner"}</DialogTitle>
          <DialogDescription>
            {opened ? <>Once the email to <b>{to}</b> has gone out from your mailbox, tap Yes so the lead shows "Owner got email".</>
              : <>Opens your email app (use sales@paywick.net) with this message to <b data-testid="owner-pitch-to">{to}</b>, BCC <b data-testid="owner-pitch-bcc">{pitch?.bcc}</b>.</>}
          </DialogDescription>
        </DialogHeader>
        {!opened && (
          <div className="grid grid-cols-3 gap-2" data-testid="owner-pitch-choices">
            {(pitch?.pitches || []).map((p) => (
              <button key={p.key} onClick={() => setKey(p.key)} data-testid={`owner-pitch-choice-${p.key}`}
                className={`h-12 rounded-xl border text-sm font-semibold transition-colors ${key === p.key ? "!bg-emerald-600 border-emerald-600 !text-white ring-2 ring-emerald-200" : "bg-white border-slate-200 text-slate-800 hover:border-emerald-400"}`}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        {!opened && chosen && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 max-h-72 overflow-y-auto">
            <p className="px-4 pt-3 text-sm font-semibold text-slate-900" data-testid="owner-pitch-subject">{chosen.subject}</p>
            <div className="p-4 space-y-3 text-sm text-slate-700" data-testid="owner-pitch-body">
              {chosen.lines.map((t) => <p key={t} className="whitespace-pre-line">{t}</p>)}
            </div>
          </div>
        )}
        {!opened && !chosen && <p className="text-xs text-slate-500" data-testid="owner-pitch-pick-hint">Pick which email to send.</p>}
        <DialogFooter>
          {opened ? (<>
            <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="owner-pitch-not-yet">Not yet</Button>
            <Button disabled={busy} onClick={confirmSent} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="owner-pitch-confirm">{busy ? "Saving…" : "Yes, I sent it"}</Button>
          </>) : (<>
            <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="owner-pitch-cancel">Cancel</Button>
            {chosen ? (
              <a href={mailtoLink(to, pitch.bcc, chosen)} onClick={() => setTimeout(() => setOpened(true), 300)} data-testid="owner-pitch-open"
                className="inline-flex items-center justify-center h-10 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
                Open in my email
              </a>
            ) : <Button disabled className="rounded-full" data-testid="owner-pitch-open-disabled">Open in my email</Button>}
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
